import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  signal,
} from '@angular/core';
import { dynamicElementsArrayRendererKey } from '@epicuro-next/constants/dynamic-elements';
import { indexIdsInObjectTree } from '@epicuro-next/dynamic-elements/utilities';
import {
  IDynamicInput,
  IDynamicInputs,
  IDynamicItem,
  IDynamicItemValue,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import {
  combineLatest,
  map,
  Observable,
  startWith,
  Subject,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';

@DynamicComponentRegister(dynamicElementsArrayRendererKey, true)
@Component({
  selector: 'epc-dynamic-elements-array-renderer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dynamic-elements-array-renderer.component.html',
  standalone: false,
})
export class DynamicElementsArrayRendererComponent
  implements OnChanges, OnDestroy
{
  private readonly changesTrigger$ = new Subject<void>();
  private readonly destroyed$ = new Subject<void>();
  private previousItemsSubscription?: Subscription;
  public dynamicItems = signal<IDynamicItem[]>([]);

  @Input() public set items$(itemValues$: Observable<unknown[]>) {
    const changesTrigger$ = this.changesTrigger$.pipe(startWith(void 0));

    if (this.previousItemsSubscription) {
      this.previousItemsSubscription.unsubscribe();
    }

    this.previousItemsSubscription = combineLatest([
      itemValues$,
      changesTrigger$,
    ])
      .pipe(
        map(([items]) => items),
        tap((items) => {
          const hasSliceStart = this.sliceStart !== undefined;
          const hasSliceEnd = this.sliceEnd !== undefined;
          if (hasSliceStart || hasSliceEnd) {
            if (!hasSliceStart) {
              this.sliceStart = 0;
            }
            items = items.slice(this.sliceStart, this.sliceEnd);
          }
          this.createDynamicItems(items);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }
  @Input() public contentType?: IDynamicItem;
  @Input() public propertyToUse?: string;
  @Input() public sliceStart?: number;
  @Input() public sliceEnd?: number;

  public get scope(): object {
    return (this as { __scope__?: object }).__scope__ ?? this;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
  }

  public ngOnChanges(): void {
    this.changesTrigger$.next(void 0);
  }

  private createDynamicItems(items?: unknown[]): void {
    if (
      this.contentType === undefined ||
      this.propertyToUse === undefined ||
      items === undefined
    ) {
      return;
    }

    const dynamicItems: IDynamicItem[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      const dataValue: IDynamicInput = item as IDynamicItemValue;
      const data: IDynamicInputs = {
        [this.propertyToUse]: dataValue,
      };

      const dynamicItem: IDynamicItem = JSON.parse(
        JSON.stringify({
          ...this.contentType,
          inputs: this.contentType.inputs,
          data: {
            ...this.contentType.data,
            ...data,
          },
        }),
      );

      indexIdsInObjectTree(dynamicItem, i);

      dynamicItems.push(dynamicItem);
    }

    this.dynamicItems.set(dynamicItems);
  }
}
